// Generate mock data for analytics
import { regions } from './mockData';

// Store types
const storeTypes = ['Café', 'Café+DT', 'Drive-thru', 'Kiosk'];

// Sales by date range
export const salesByDate = {
  daily: Array.from({ length: 30 }, (_, i) => {
    const date = new Date();
    date.setDate(date.getDate() - (29 - i));
    return {
      date: date.toISOString().split('T')[0],
      netSales: 300000 + Math.random() * 200000,
      salesWithVAT: (300000 + Math.random() * 200000) * 1.15,
      transactions: 1500 + Math.random() * 1000,
      avgTicket: 150 + Math.random() * 50,
    };
  }),

  weekly: Array.from({ length: 12 }, (_, i) => {
    const date = new Date();
    date.setDate(date.getDate() - ((11 - i) * 7));
    return {
      weekStart: date.toISOString().split('T')[0],
      netSales: 2100000 + Math.random() * 1200000,
      salesWithVAT: (2100000 + Math.random() * 1200000) * 1.15,
      transactions: 10500 + Math.random() * 3000,
      avgTicket: 150 + Math.random() * 50,
    };
  }),

  monthly: Array.from({ length: 12 }, (_, i) => {
    const date = new Date();
    date.setMonth(date.getMonth() - (11 - i));
    return {
      month: date.toLocaleString('en-US', { month: 'long', year: 'numeric' }),
      netSales: 9000000 + Math.random() * 4000000,
      salesWithVAT: (9000000 + Math.random() * 4000000) * 1.15,
      transactions: 45000 + Math.random() * 15000,
      avgTicket: 150 + Math.random() * 50,
    };
  }),

  quarterly: Array.from({ length: 8 }, (_, i) => {
    const date = new Date();
    const quarter = Math.floor((date.getMonth() - (7 - i) * 3) / 3) + 1;
    const year = date.getFullYear() + Math.floor((date.getMonth() - (7 - i) * 3) / 12);
    return {
      quarter: `Q${quarter} ${year}`,
      netSales: 27000000 + Math.random() * 8000000,
      salesWithVAT: (27000000 + Math.random() * 8000000) * 1.15,
      transactions: 135000 + Math.random() * 30000,
      avgTicket: 150 + Math.random() * 50,
    };
  }),

  yearly: Array.from({ length: 5 }, (_, i) => {
    const year = new Date().getFullYear() - (4 - i);
    return {
      year: year.toString(),
      netSales: 108000000 + Math.random() * 25000000,
      salesWithVAT: (108000000 + Math.random() * 25000000) * 1.15,
      transactions: 540000 + Math.random() * 100000,
      avgTicket: 150 + Math.random() * 50,
    };
  }),
};

// Store performance data
export const storePerformance = regions.flatMap(region => 
  region.stores.map(store => ({
    id: store.id,
    name: store.name,
    region: region.name,
    type: storeTypes[Math.floor(Math.random() * storeTypes.length)],
    location: {
      coordinates: store.coordinates,
      city: store.name.includes('Riyadh') ? 'Riyadh' : 
            store.name.includes('Jeddah') ? 'Jeddah' : 
            store.name.includes('Dammam') || store.name.includes('Dhahran') ? 'Eastern Province' :
            'Other',
    },
    sales: store.sales,
    transactions: Math.round(store.sales / (120 + Math.random() * 30)),
    avgTicket: 120 + Math.random() * 30,
    refundRate: 1 + Math.random() * 3,
    discountRate: 2 + Math.random() * 5,
    growth: store.growth,
    rank: Math.floor(Math.random() * 100) + 1,
  }))
);

// Product categories
const productCategories = [
  'Hot Coffee', 'Cold Coffee', 'Tea', 'Pastries', 
  'Sandwiches', 'Desserts', 'Beverages', 'Breakfast'
];

// Product sizes
const productSizes = ['8 oz', '12 oz', '16 oz', '20 oz', 'Regular', 'Large'];

// Generate product mix data
export const productMix = Array.from({ length: 80 }, (_, i) => {
  const category = productCategories[Math.floor(Math.random() * productCategories.length)];
  const size = category.includes('Coffee') || category.includes('Tea') || category.includes('Beverages') ? 
    productSizes[Math.floor(Math.random() * 4)] : 
    productSizes[Math.floor(Math.random() * 2) + 4];
  
  // Simulate some duplicates for the duplicate detection feature
  const nameBase = [
    'Espresso', 'Latte', 'Cappuccino', 'Mocha', 'Americano',
    'Cold Brew', 'Iced Coffee', 'Frappuccino', 'Turkish Coffee', 'Cortado',
    'Croissant', 'Muffin', 'Sandwich', 'Cake', 'Cookie',
    'Tea', 'Chai', 'Hot Chocolate', 'Water', 'Juice'
  ][Math.floor(Math.random() * 20)];
  
  const name = i % 20 === 0 ? nameBase : `${nameBase} ${['Classic', 'Premium', 'Special', 'Signature', 'House'][Math.floor(Math.random() * 5)]}`;
  
  return {
    id: i + 1,
    name,
    category,
    size,
    price: 10 + Math.random() * 30,
    cost: (10 + Math.random() * 30) * 0.3,
    quantity: 1000 + Math.random() * 10000,
    sales: (10 + Math.random() * 30) * (1000 + Math.random() * 10000),
    margin: 70 - Math.random() * 20,
    growth: Math.random() > 0.3 ? 5 + Math.random() * 20 : -5 - Math.random() * 10,
  };
});

// Hourly sales data
export const hourlySales = Array.from({ length: 24 }, (_, hour) => {
  // Create a natural bell curve peaking at lunch and evening times
  const lunchFactor = Math.exp(-0.5 * Math.pow((hour - 13) / 2.5, 2));
  const eveningFactor = Math.exp(-0.5 * Math.pow((hour - 19) / 2.5, 2));
  const baseFactor = 0.2;
  const factor = baseFactor + lunchFactor * 0.8 + eveningFactor * 0.7;
  
  return {
    hour: `${hour}:00`,
    sales: 5000 + factor * 45000 + Math.random() * 5000,
    transactions: 25 + factor * 175 + Math.random() * 25,
    customers: 20 + factor * 150 + Math.random() * 25,
  };
});

// Weekly hourly heatmap
export const weeklyHourlyHeatmap = Array.from({ length: 7 }, (_, day) => {
  const dayName = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][day];
  
  return {
    day: dayName,
    hours: Array.from({ length: 24 }, (_, hour) => {
      // Weekend vs weekday patterns
      const isWeekend = day === 0 || day === 5 || day === 6;
      const lunchFactor = Math.exp(-0.5 * Math.pow((hour - 13) / 2.5, 2));
      const eveningFactor = Math.exp(-0.5 * Math.pow((hour - (isWeekend ? 20 : 19)) / 2.5, 2));
      const morningFactor = Math.exp(-0.5 * Math.pow((hour - 8) / 1.5, 2));
      
      const baseFactor = 0.2;
      const factor = baseFactor + 
        lunchFactor * (isWeekend ? 0.7 : 0.8) + 
        eveningFactor * (isWeekend ? 0.9 : 0.7) +
        morningFactor * (isWeekend ? 0.6 : 0.9);
      
      return {
        hour: `${hour}:00`,
        value: factor * 100, // 0-100 scale for heatmap
        sales: 5000 + factor * 45000 + Math.random() * 5000,
        transactions: 25 + factor * 175 + Math.random() * 25,
      };
    })
  };
});

// Refund data
export const refundData = Array.from({ length: 100 }, (_, i) => {
  const date = new Date();
  date.setDate(date.getDate() - Math.floor(Math.random() * 30));
  
  const reasons = [
    'Customer Dissatisfaction', 
    'Wrong Order', 
    'Quality Issue', 
    'Late Delivery', 
    'Changed Mind',
    'Payment Error'
  ];
  
  const stores = storePerformance.map(store => ({ id: store.id, name: store.name }));
  const selectedStore = stores[Math.floor(Math.random() * stores.length)];
  
  return {
    id: i + 1,
    date: date.toISOString().split('T')[0],
    time: Math.random() > 0.1 ? // 10% missing timestamps for the audit flag feature
      `${Math.floor(Math.random() * 24)}:${Math.floor(Math.random() * 60).toString().padStart(2, '0')}` :
      null,
    amount: 20 + Math.random() * 200,
    reason: reasons[Math.floor(Math.random() * reasons.length)],
    storeId: selectedStore.id,
    storeName: selectedStore.name,
    user: ['Ahmed M.', 'Fatima K.', 'Omar S.', 'Layla A.', 'Khalid R.'][Math.floor(Math.random() * 5)],
  };
});

// Discount data
export const discountData = Array.from({ length: 100 }, (_, i) => {
  const date = new Date();
  date.setDate(date.getDate() - Math.floor(Math.random() * 30));
  
  const types = [
    'Promotion', 
    'Loyalty Program', 
    'Staff Discount', 
    'Bulk Purchase', 
    'Special Event',
    'Manager Override'
  ];
  
  const stores = storePerformance.map(store => ({ id: store.id, name: store.name }));
  const selectedStore = stores[Math.floor(Math.random() * stores.length)];
  
  return {
    id: i + 1,
    date: date.toISOString().split('T')[0],
    amount: 15 + Math.random() * 100,
    percentage: 5 + Math.random() * 20,
    type: types[Math.floor(Math.random() * types.length)],
    storeId: selectedStore.id,
    storeName: selectedStore.name,
    user: ['Ahmed M.', 'Fatima K.', 'Omar S.', 'Layla A.', 'Khalid R.'][Math.floor(Math.random() * 5)],
  };
});

// Promotion impact data
export const promotionImpact = Array.from({ length: 10 }, (_, i) => {
  const startDate = new Date();
  startDate.setDate(startDate.getDate() - 30 - Math.floor(Math.random() * 30));
  
  const endDate = new Date(startDate);
  endDate.setDate(endDate.getDate() + 7 + Math.floor(Math.random() * 14));
  
  const beforeSales = 500000 + Math.random() * 300000;
  const multiplier = 1 + Math.random() * 0.5; // 0-50% increase
  
  return {
    id: i + 1,
    name: [
      'Summer Special', 'Happy Hour', 'Weekend Deal', 
      'Buy One Get One', 'Loyalty Reward', 'New Product Launch',
      'Holiday Special', 'App Exclusive', 'Flash Sale', 'Bundle Offer'
    ][i],
    startDate: startDate.toISOString().split('T')[0],
    endDate: endDate.toISOString().split('T')[0],
    beforeSales,
    afterSales: beforeSales * multiplier,
    targetProduct: productMix[Math.floor(Math.random() * productMix.length)].name,
    discountPercentage: 10 + Math.floor(Math.random() * 40),
    storeIds: Array.from({ length: 3 + Math.floor(Math.random() * 5) }, () => 
      storePerformance[Math.floor(Math.random() * storePerformance.length)].id
    ),
  };
});

// Manager performance data
export const managerPerformance = {
  regional: Array.from({ length: 5 }, (_, i) => {
    const nameBase = ['Ahmed', 'Mohammed', 'Abdullah', 'Sara', 'Fatima'][i];
    const surnameBase = ['Al-Saud', 'Al-Qahtani', 'Al-Ghamdi', 'Al-Rashid', 'Al-Otaibi'][i];
    
    return {
      id: `RM${i + 1}`,
      name: `${nameBase} ${surnameBase}`,
      region: ['Riyadh Region', 'Eastern Province', 'Makkah Region', 'Asir Region', 'Tabuk Region'][i],
      sales: 200000000 + Math.random() * 100000000,
      stores: 15 + Math.floor(Math.random() * 15),
      avgStorePerformance: 85 + Math.random() * 15,
      topStore: storePerformance[Math.floor(Math.random() * storePerformance.length)].name,
      growth: 5 + Math.random() * 15,
    };
  }),
  
  district: Array.from({ length: 12 }, (_, i) => {
    const nameBase = [
      'Khalid', 'Omar', 'Ali', 'Noura', 'Layla', 'Hassan',
      'Ibrahim', 'Amal', 'Youssef', 'Reem', 'Saad', 'Maryam'
    ][i];
    
    const surnameBase = [
      'Al-Harbi', 'Al-Zahrani', 'Al-Shehri', 'Al-Asmari', 'Al-Dosari', 'Al-Ahmadi',
      'Al-Mutairi', 'Al-Sulaiman', 'Al-Tuwaijri', 'Al-Malik', 'Al-Farsi', 'Al-Juhani'
    ][i];
    
    return {
      id: `DM${i + 1}`,
      name: `${nameBase} ${surnameBase}`,
      district: [
        'North Riyadh', 'South Riyadh', 'East Riyadh', 'West Riyadh',
        'North Jeddah', 'South Jeddah', 'Dammam', 'Khobar',
        'Makkah', 'Taif', 'Abha', 'Tabuk'
      ][i],
      sales: 80000000 + Math.random() * 40000000,
      stores: 5 + Math.floor(Math.random() * 10),
      avgStorePerformance: 85 + Math.random() * 15,
      topStore: storePerformance[Math.floor(Math.random() * storePerformance.length)].name,
      growth: 5 + Math.random() * 15,
    };
  }),
};

// Sales forecasts and projections
export const salesForecasts = {
  daily: Array.from({ length: 30 }, (_, i) => {
    const date = new Date();
    date.setDate(date.getDate() + i);
    
    // Add some realistic patterns
    const isWeekend = date.getDay() === 5 || date.getDay() === 6;
    const isHoliday = Math.random() < 0.05; // 5% chance of being a holiday
    const seasonalFactor = 1 + 0.2 * Math.sin(2 * Math.PI * (date.getMonth() + date.getDate()/30) / 12);
    
    const baseSales = 300000 + Math.random() * 50000;
    const forecastSales = baseSales * 
      (isWeekend ? 1.3 : 1) * 
      (isHoliday ? 0.8 : 1) *
      seasonalFactor;
    
    // Add confidence intervals
    const lowerBound = forecastSales * (0.9 - Math.random() * 0.1);
    const upperBound = forecastSales * (1.1 + Math.random() * 0.1);
    
    return {
      date: date.toISOString().split('T')[0],
      forecast: forecastSales,
      lowerBound,
      upperBound,
      isWeekend,
      isHoliday,
      anomalyScore: Math.random() < 0.1 ? 0.7 + Math.random() * 0.3 : 0, // 10% chance of anomaly
      insight: isWeekend ? 'Weekend uplift expected' : 
               isHoliday ? 'Holiday - expect reduced traffic' : 
               Math.random() < 0.2 ? ['Promotion impact', 'Seasonal effect', 'School holiday', 'Weather impact'][Math.floor(Math.random() * 4)] : 
               null
    };
  }),
  
  monthly: Array.from({ length: 12 }, (_, i) => {
    const date = new Date();
    date.setMonth(date.getMonth() + i);
    
    // Add seasonal patterns
    const month = date.getMonth();
    const seasonalFactors = [0.9, 0.95, 1, 1.05, 1.1, 1.15, 1.2, 1.1, 1, 0.95, 1.1, 1.2]; // Jan-Dec
    
    const baseSales = 9000000 + Math.random() * 1000000;
    const forecastSales = baseSales * seasonalFactors[month];
    
    // Add confidence intervals
    const lowerBound = forecastSales * (0.85 - Math.random() * 0.1);
    const upperBound = forecastSales * (1.15 + Math.random() * 0.1);
    
    return {
      month: date.toLocaleString('en-US', { month: 'long', year: 'numeric' }),
      forecast: forecastSales,
      lowerBound,
      upperBound,
      seasonality: seasonalFactors[month],
      insight: month === 11 ? 'Holiday season peak expected' :
               month === 6 ? 'Summer vacation boost' :
               month === 8 ? 'Back to school period' :
               month === 0 ? 'Post-holiday slowdown likely' :
               null
    };
  })
};

// Report templates for custom report builder
export const reportTemplates = [
  {
    id: 'sales-overview',
    name: 'Sales Overview',
    description: 'Comprehensive view of sales performance across regions and time periods',
    metrics: ['netSales', 'salesWithVAT', 'transactions', 'avgTicket', 'growth'],
    dimensions: ['date', 'region', 'storeType'],
    visualizations: ['line', 'bar', 'pie'],
    dateRanges: ['daily', 'weekly', 'monthly', 'quarterly', 'yearly'],
  },
  {
    id: 'store-comparison',
    name: 'Store Comparison',
    description: 'Side-by-side comparison of store performance metrics',
    metrics: ['sales', 'transactions', 'avgTicket', 'refundRate', 'discountRate'],
    dimensions: ['store', 'region', 'storeType'],
    visualizations: ['bar', 'radar', 'table'],
    dateRanges: ['daily', 'weekly', 'monthly'],
  },
  {
    id: 'product-performance',
    name: 'Product Performance',
    description: 'Analysis of product sales, margins, and trends',
    metrics: ['sales', 'quantity', 'margin', 'growth'],
    dimensions: ['product', 'category', 'size'],
    visualizations: ['bar', 'scatter', 'treemap'],
    dateRanges: ['weekly', 'monthly', 'quarterly'],
  },
  {
    id: 'hourly-analysis',
    name: 'Hourly Analysis',
    description: 'Detailed breakdown of sales performance by hour and day of week',
    metrics: ['sales', 'transactions', 'customers'],
    dimensions: ['hour', 'dayOfWeek'],
    visualizations: ['heatmap', 'line', 'bar'],
    dateRanges: ['daily', 'weekly'],
  },
  {
    id: 'refund-analysis',
    name: 'Refund Analysis',
    description: 'Detailed analysis of refunds by reason, store, and user',
    metrics: ['amount', 'count', 'percentageOfSales'],
    dimensions: ['reason', 'store', 'user', 'date'],
    visualizations: ['pie', 'bar', 'table'],
    dateRanges: ['daily', 'weekly', 'monthly'],
  },
];

// Available metrics and dimensions for custom report builder
export const reportComponents = {
  metrics: [
    { id: 'netSales', name: 'Net Sales', format: 'currency' },
    { id: 'salesWithVAT', name: 'Sales (incl. VAT)', format: 'currency' },
    { id: 'transactions', name: 'Transactions', format: 'number' },
    { id: 'customers', name: 'Customers', format: 'number' },
    { id: 'avgTicket', name: 'Average Ticket', format: 'currency' },
    { id: 'refundRate', name: 'Refund Rate', format: 'percent' },
    { id: 'discountRate', name: 'Discount Rate', format: 'percent' },
    { id: 'growth', name: 'YoY Growth', format: 'percent' },
    { id: 'quantity', name: 'Quantity Sold', format: 'number' },
    { id: 'margin', name: 'Profit Margin', format: 'percent' },
    { id: 'salesPerSqMeter', name: 'Sales per sq. meter', format: 'currency' },
    { id: 'salesPerHour', name: 'Sales per Hour', format: 'currency' },
    { id: 'unitsPerTransaction', name: 'Units per Transaction', format: 'decimal' },
  ],
  
  dimensions: [
    { id: 'date', name: 'Date', type: 'date' },
    { id: 'store', name: 'Store', type: 'string' },
    { id: 'region', name: 'Region', type: 'string' },
    { id: 'storeType', name: 'Store Type', type: 'string' },
    { id: 'product', name: 'Product', type: 'string' },
    { id: 'category', name: 'Category', type: 'string' },
    { id: 'size', name: 'Size', type: 'string' },
    { id: 'hour', name: 'Hour', type: 'number' },
    { id: 'dayOfWeek', name: 'Day of Week', type: 'string' },
    { id: 'user', name: 'User', type: 'string' },
    { id: 'city', name: 'City', type: 'string' },
    { id: 'district', name: 'District', type: 'string' },
    { id: 'manager', name: 'Manager', type: 'string' },
  ],
  
  visualizations: [
    { id: 'bar', name: 'Bar Chart' },
    { id: 'line', name: 'Line Chart' },
    { id: 'pie', name: 'Pie Chart' },
    { id: 'scatter', name: 'Scatter Plot' },
    { id: 'table', name: 'Data Table' },
    { id: 'heatmap', name: 'Heat Map' },
    { id: 'radar', name: 'Radar Chart' },
    { id: 'treemap', name: 'Tree Map' },
    { id: 'map', name: 'Geographic Map' },
  ],
  
  dateRanges: [
    { id: 'daily', name: 'Daily' },
    { id: 'weekly', name: 'Weekly' },
    { id: 'monthly', name: 'Monthly' },
    { id: 'quarterly', name: 'Quarterly' },
    { id: 'yearly', name: 'Yearly' },
    { id: 'custom', name: 'Custom Range' },
  ],
}; 